class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (this.classList.contains('active')) return;
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        if (!this.isConnected || !this.classList.contains('active')) return;
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty') || this.querySelector('#CartDrawer')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        if (containerToTrapFocusOn) trapFocus(containerToTrapFocusOn, focusElement || containerToTrapFocusOn);
      },
      { once: true },
    );

    document.body.classList.add('overflow-hidden');

    // cart-drawer-items is a CartItems subclass that extends createViewEventElement.
    // Its `view-event-trigger="manual"` skips auto-dispatch on connect; we fire
    // it here when the drawer opens, with `context: 'dialog'` from the payload attribute.
    this.querySelector('cart-drawer-items')?.dispatchViewEvent();
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });
    this.classList.toggle('is-empty', Boolean(this.querySelector('.drawer__inner-empty, .pt-cart-empty')));

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  async animateAddition(button) {
    if (this.classList.contains('active') || document.hidden || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    const cartLink = document.querySelector('[data-ai-header-cart-link], #cart-icon-bubble');
    const target = cartLink?.querySelector('svg') || cartLink;
    if (!button?.isConnected || !target || typeof Element.prototype.animate !== 'function') return;
    const from = button.getBoundingClientRect();
    const to = target.getBoundingClientRect();
    const inView = (rect) => rect.width > 0 && rect.height > 0 && rect.bottom > 0 && rect.top < innerHeight && rect.right > 0 && rect.left < innerWidth;
    if (!inView(from) || !inView(to)) return;

    const layer = document.createElement('div');
    layer.setAttribute('data-pt-cart-flight', '');
    layer.setAttribute('aria-hidden', 'true');
    layer.style.cssText = 'position:fixed;inset:0;pointer-events:none;z-index:2147483000;overflow:visible;';
    const start = { x: from.left + from.width / 2, y: from.top + from.height / 2 };
    const end = { x: to.left + to.width / 2, y: to.top + to.height / 2 };
    const bend = { x: start.x + (end.x - start.x) * 0.2, y: Math.max(12, Math.min(start.y, end.y) - 80) };
    const animations = [];
    const cancel = () => animations.forEach((animation) => animation.cancel());
    const onVisibility = () => { if (document.hidden) cancel(); };
    const motion = window.matchMedia('(prefers-reduced-motion: reduce)');
    const onMotion = () => { if (motion.matches) cancel(); };

    try {
      document.body.appendChild(layer);
      window.addEventListener('resize', cancel, { once: true });
      window.addEventListener('scroll', cancel, { once: true, passive: true });
      document.addEventListener('visibilitychange', onVisibility);
      motion.addEventListener('change', onMotion);
      for (let index = 0; index < 5; index++) {
        const light = document.createElement('span');
        const size = index === 0 ? 10 : 7 - index * 0.7;
        light.style.cssText = `position:absolute;left:0;top:0;width:${size}px;height:${size}px;border-radius:50%;background:radial-gradient(circle,#fffdf7 0 20%,#eed6ad 42%,#9c596c 72%,transparent 100%);box-shadow:0 0 9px 2px rgba(156,89,108,.3);`;
        layer.appendChild(light);
        const frames = Array.from({ length: 25 }, (_, step) => {
          const t = step / 24;
          const x = (1 - t) ** 2 * start.x + 2 * (1 - t) * t * bend.x + t ** 2 * end.x;
          const y = (1 - t) ** 2 * start.y + 2 * (1 - t) * t * bend.y + t ** 2 * end.y;
          return { transform: `translate(${x - size / 2}px,${y - size / 2}px) scale(${1 - t * 0.35})`, opacity: (1 - index * 0.15) * (t > 0.9 ? (1 - t) * 10 : 1) };
        });
        animations.push(light.animate(frames, { duration: 620, delay: index * 18, easing: 'cubic-bezier(.25,.1,.3,1)', fill: 'both' }));
      }
      await Promise.all(animations.map((animation) => animation.finished));
    } catch (_error) {
    } finally {
      cancel();
      layer.remove();
      window.removeEventListener('resize', cancel);
      window.removeEventListener('scroll', cancel);
      document.removeEventListener('visibilitychange', onVisibility);
      motion.removeEventListener('change', onMotion);
    }
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);

